import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPlantOperationsData, savePlantOperationsForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { PLANT_OPERATIONS } from '../../../formQuestionsEnum';
import PhasesForm from './PhasesForm';

const PlantOperationsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const plantOperationsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.plantOperationsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getPlantOperationsData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(PLANT_OPERATIONS).map(([, v]) => [v.id, v.fieldName]));

    const mappedFieldsRaw = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    const mappedFields = {
      ...mappedFieldsRaw,
      warrantiedEquipment:
        mappedFieldsRaw.warrantiedEquipment?.length > 0
          ? mappedFieldsRaw.warrantiedEquipment
          : [{ id: 0, name: '', deleteFlag: false }],
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [setFormData, data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await savePlantOperationsForm({ id, sellerFacilityId: facilityId, ...result })
      .then((resp) => {
        enqueueSnackbar('Plant Operations data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Plant Operations data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <PhasesForm permissions={plantOperationsPermissions} />
  );
});

export default PlantOperationsView;

PlantOperationsView.propTypes = {
  setFormData: PropTypes.func,
};
